import React, { useState, useEffect } from 'react';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import { FaPlus, FaMoneyBill, FaMinus, FaCalendarAlt, FaTrash, FaCalculator, FaEye } from 'react-icons/fa';
import { toast } from 'react-toastify';
import { useAuth } from '../../contexts/AuthContext';
import CierreDiarioPanel from '../../components/caja/CierreDiarioPanel';
import cajaService from '../../services/caja.service';
import { canCerrarCaja, canCorregirCierreCaja, canVerHistorialCierres, canVerResumenCaja, canEliminarMovimientoCaja } from '../../utils/cajaPermisos';
import { fechaDiaNegocioLocal } from '../../utils/fechaDiaNegocio';
import { setCajaOperacionEnCurso } from '../../utils/criticalOperationGuard';
import { useSafeUpdateOperationGuard } from '../../hooks/useSafeUpdateFormGuard';
import { BILLETERA_VIRTUAL_LABEL } from '../../utils/metodoPagoLabels';
import { FaCreditCard, FaUniversity, FaMobileAlt } from 'react-icons/fa';

const formatMovimientoHora = (mov) => {
  const fc = mov?.fechaCreacion;
  const ms = mov?.fechaCreacionMs
    || (fc && typeof fc.toMillis === 'function' ? fc.toMillis() : null)
    || (fc && typeof fc === 'object' ? (fc.seconds ?? fc._seconds) * 1000 : null);
  if (ms) {
    return new Date(ms).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
  }
  if (mov?.hora != null && mov.hora !== '') return String(mov.hora);
  return '—';
};

/** Misma resolución que CierreDiarioPanel → snapshot.efectivoEsperado (sin recalcular). */
const efectivoEsperadoDesdeCierreDia = (body) => {
  const cierre = body?.cierre;
  const yaCerrado = body?.cierreCerrado === true || Boolean(cierre);
  const snapshot = yaCerrado
    ? (cierre?.snapshotAlCierre || body?.snapshot || {})
    : (body?.snapshot || {});
  return parseFloat(snapshot.efectivoEsperado ?? 0) || 0;
};

const Caja = () => {
  const { orgId, sucursalSeleccionada, hasPermission } = useAuth();
  const sucursalId = sucursalSeleccionada?.id || null;
  const [fecha, setFecha] = useState(() => fechaDiaNegocioLocal());
  const [movimientos, setMovimientos] = useState([]);
  const [resumen, setResumen] = useState({ ingresos: 0, egresos: 0, saldo: 0 });
  const [efectivoEsperadoCierre, setEfectivoEsperadoCierre] = useState(0);
  const [saldoAcumulado, setSaldoAcumulado] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    tipo: 'ingreso',
    monto: '',
    concepto: '',
    observaciones: '',
    medio_pago: 'efectivo'
  });
  const [agregando, setAgregando] = useState(false);
  const [eliminando, setEliminando] = useState(null);
  const [modoVista, setModoVista] = useState('diario');
  const [verificandoSaldo, setVerificandoSaldo] = useState(false);
  const [saldoFisico, setSaldoFisico] = useState('');
  const [mostrarVerificacion, setMostrarVerificacion] = useState(false);

  const cajaOperacionActiva = agregando
    || eliminando !== null
    || mostrarVerificacion
    || Boolean(String(form.monto || '').trim())
    || Boolean(String(form.concepto || '').trim());
  useSafeUpdateOperationGuard(cajaOperacionActiva, setCajaOperacionEnCurso);

  useEffect(() => {
    if (!orgId || !sucursalId) return;

    if (modoVista === 'diario') {
      cargarMovimientos();
      cargarResumen();
    } else {
      cargarMovimientosAcumulados();
      cargarSaldoAcumulado();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fecha, modoVista, orgId, sucursalId]);

  const cargarMovimientos = async () => {
    if (!orgId || !sucursalId) return;

    setLoading(true);
    try {
      const data = await cajaService.obtenerMovimientos({ fecha, sucursalId });
      setMovimientos(cajaService.ensureArray(data));
    } catch (e) {
      console.error('❌ [FRONTEND] Error cargando movimientos:', e);
      toast.error(e?.message || 'Error al cargar movimientos');
      setMovimientos([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarMovimientosAcumulados = async () => {
    if (!orgId || !sucursalId) return;

    setLoading(true);
    try {
      const data = await cajaService.obtenerMovimientosAcumulados({ sucursalId });
      setMovimientos(data);
    } catch (e) {
      console.error('❌ [FRONTEND] Error cargando movimientos acumulados:', e);
      toast.error(e?.message || 'Error al cargar movimientos acumulados');
      setMovimientos([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarSaldoAcumulado = async () => {
    if (!orgId || !sucursalId) return;

    try {
      const data = await cajaService.obtenerSaldoAcumulado({ sucursalId });
      setSaldoAcumulado(data?.saldoAcumulado || 0);
    } catch (e) {
      console.error('❌ [FRONTEND] Error cargando saldo acumulado:', e);
      setSaldoAcumulado(0);
    }
  };

  const verificarSaldoFisico = async () => {
    if (!saldoFisico || isNaN(parseFloat(saldoFisico))) {
      toast.warning('Ingresa un monto válido para el saldo físico');
      return;
    }
    if (!sucursalId) {
      toast.warning('Seleccioná una sucursal');
      return;
    }

    setVerificandoSaldo(true);
    try {
      const data = await cajaService.verificarSaldoFisico({
        sucursalId,
        saldoFisico: parseFloat(saldoFisico)
      });

      if (data?.coinciden) {
        toast.success('✅ Saldo físico coincide con el sistema');
      } else {
        toast.warning(`⚠️ Diferencia encontrada: $${Number(data?.diferencia || 0).toFixed(2)}`);
      }

      setMostrarVerificacion(false);
      setSaldoFisico('');
    } catch (e) {
      console.error('❌ [FRONTEND] Error verificando saldo:', e);
      toast.error(e?.message || 'Error al verificar saldo');
    } finally {
      setVerificandoSaldo(false);
    }
  };

  const cargarResumen = async () => {
    if (!orgId || !sucursalId) return;

    try {
      const [data, cierreDia] = await Promise.all([
        cajaService.obtenerResumen({ fecha, sucursalId }),
        cajaService.obtenerCierreDia({ fecha, sucursalId })
      ]);
      setResumen({
        ingresos: data?.ingresos || 0,
        egresos: data?.egresos || 0,
        saldo: data?.saldo || 0,
        ingresosPorMedio: data?.ingresosPorMedio || {},
        egresosPorMedio: data?.egresosPorMedio || {}
      });
      setEfectivoEsperadoCierre(efectivoEsperadoDesdeCierreDia(cierreDia));
    } catch (e) {
      console.error('❌ [FRONTEND] Error cargando resumen:', e);
      setResumen({ ingresos: 0, egresos: 0, saldo: 0, ingresosPorMedio: {}, egresosPorMedio: {} });
      setEfectivoEsperadoCierre(0);
    }
  };

  const handleFormChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleAgregar = async e => {
    e.preventDefault();
    if (!form.monto || !form.concepto) {
      toast.warning('Completa todos los campos obligatorios');
      return;
    }
    if (!sucursalId) {
      toast.warning('Seleccioná una sucursal');
      return;
    }

    setAgregando(true);
    try {
      await cajaService.agregarMovimiento({
        ...form,
        monto: parseFloat(form.monto),
        fecha,
        sucursal_id: sucursalId
      }, { sucursalId });

      toast.success(`${form.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'} agregado correctamente`);
      setForm({ tipo: 'ingreso', monto: '', concepto: '', observaciones: '', medio_pago: 'efectivo' });

      if (modoVista === 'diario') {
        await cargarMovimientos();
        await cargarResumen();
      } else {
        await cargarMovimientosAcumulados();
        await cargarSaldoAcumulado();
      }
    } catch (e) {
      console.error('❌ [FRONTEND] Error agregando movimiento:', e);
      toast.error(e?.message || 'Error al agregar movimiento');
    } finally {
      setAgregando(false);
    }
  };

  const handleEliminar = async (movimientoId) => {
    if (!canEliminarMovimientoCaja(hasPermission)) {
      toast.error('No tienes permisos para eliminar movimientos de caja');
      return;
    }
    const motivo = window.prompt('Motivo de eliminación (obligatorio):');
    if (!motivo || motivo.trim().length < 3) {
      toast.warning('Debes indicar un motivo de al menos 3 caracteres');
      return;
    }
    if (!window.confirm('¿Estás seguro de eliminar este movimiento manual?')) return;
    if (!sucursalId) return;

    setEliminando(movimientoId);
    try {
      await cajaService.eliminarMovimiento(movimientoId, { sucursalId, motivo: motivo.trim() });
      toast.success('Movimiento eliminado correctamente');

      if (modoVista === 'diario') {
        await cargarMovimientos();
        await cargarResumen();
      } else {
        await cargarMovimientosAcumulados();
        await cargarSaldoAcumulado();
      }
    } catch (e) {
      console.error('Error eliminando movimiento:', e);
      toast.error(e?.message || 'Error al eliminar movimiento');
    } finally {
      setEliminando(null);
    }
  };

  if (!sucursalId) {
    return (
      <div className="max-w-6xl mx-auto p-4">
        <p className="text-e0e3-muted">Seleccioná una sucursal para ver la caja.</p>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto p-4">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-e0e3-foreground flex items-center gap-3">
          <FaMoneyBill className="text-green-600" /> Caja Chica
        </h1>
        <p className="text-e0e3-muted mt-1">
          Gestión de ingresos y egresos — {sucursalSeleccionada?.nombre || sucursalId}
        </p>
      </div>

      {/* Selector de modo de vista */}
      <div className="mb-6">
        <div className="flex items-center gap-4">
          <div className="flex rounded-lg border border-white/10 bg-black/35 p-1">
            <button
              onClick={() => setModoVista('diario')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                modoVista === 'diario'
                  ? 'bg-e0e3-accent/15 text-e0e3-accent shadow-sm'
                  : 'text-e0e3-muted hover:text-e0e3-foreground'
              }`}
            >
              Vista Diaria
            </button>
            <button
              onClick={() => setModoVista('acumulado')}
              className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                modoVista === 'acumulado'
                  ? 'bg-e0e3-accent/15 text-e0e3-accent shadow-sm'
                  : 'text-e0e3-muted hover:text-e0e3-foreground'
              }`}
            >
              Caja Chica (Acumulado)
            </button>
          </div>

          {modoVista === 'acumulado' && (
            <button
              onClick={() => setMostrarVerificacion(true)}
              className="flex items-center gap-2 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
            >
              <FaCalculator />
              Verificar Saldo Físico
            </button>
          )}
        </div>
      </div>

      {/* Selector de fecha - Solo en modo diario */}
      {modoVista === 'diario' && (
        <div className="mb-6">
          <label className="block text-sm font-medium text-e0e3-muted mb-2">Fecha a consultar</label>
          <div className="flex items-center gap-3">
            <FaCalendarAlt className="text-e0e3-muted/80" />
            <input
              type="date"
              value={fecha}
              onChange={e => setFecha(e.target.value)}
              className="nexo-field rounded-lg px-4 py-2"
            />
          </div>
        </div>
      )}

      {/* Resumen */}
      {modoVista === 'diario' ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <div className="rounded-lg border border-emerald-500/30 bg-emerald-500/10 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-emerald-300 text-sm font-medium">Ingresos del día</p>
                <p className="text-emerald-200 text-2xl font-bold">+${resumen.ingresos.toFixed(2)}</p>
              </div>
              <FaPlus className="text-green-500 text-xl" />
            </div>
          </div>

          <div className="rounded-lg border border-red-500/30 bg-red-500/10 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-red-300 text-sm font-medium">Egresos del día</p>
                <p className="text-red-200 text-2xl font-bold">-${resumen.egresos.toFixed(2)}</p>
              </div>
              <FaMinus className="text-red-500 text-xl" />
            </div>
          </div>

          <div className={`rounded-lg p-6 shadow-sm border ${
            resumen.saldo >= 0
              ? 'border-blue-500/30 bg-blue-500/10'
              : 'border-amber-500/30 bg-amber-500/10'
          }`}>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-e0e3-muted text-sm font-medium">Saldo del día</p>
                <p className={`text-2xl font-bold ${resumen.saldo >= 0 ? 'text-blue-200' : 'text-amber-200'}`}>
                  {resumen.saldo >= 0 ? '+' : ''}${resumen.saldo.toFixed(2)}
                </p>
              </div>
              <FaMoneyBill className={`text-xl ${resumen.saldo >= 0 ? 'text-blue-500' : 'text-yellow-500'}`} />
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <div className="rounded-lg border border-purple-500/30 bg-purple-500/10 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-purple-300 text-sm font-medium">Saldo Acumulado</p>
                <p className={`text-3xl font-bold ${saldoAcumulado >= 0 ? 'text-purple-200' : 'text-red-200'}`}>
                  {saldoAcumulado >= 0 ? '+' : ''}${saldoAcumulado.toFixed(2)}
                </p>
                <p className="text-purple-300/80 text-xs mt-1">Total en caja chica</p>
              </div>
              <FaMoneyBill className="text-purple-500 text-2xl" />
            </div>
          </div>

          <div className="rounded-lg border border-orange-500/30 bg-orange-500/10 p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-300 text-sm font-medium">Movimientos</p>
                <p className="text-orange-200 text-2xl font-bold">{movimientos.length}</p>
                <p className="text-orange-300/80 text-xs mt-1">Total registrados</p>
              </div>
              <FaEye className="text-orange-500 text-2xl" />
            </div>
          </div>
        </div>
      )}

      {/* Tarjetas por medio de pago (ingresos) */}
      {modoVista === 'diario' && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="rounded-lg border border-white/10 bg-e0e3-surface/95 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-e0e3-muted text-sm">Efectivo</p>
                <p className="text-2xl font-bold text-e0e3-foreground">
                  ${efectivoEsperadoCierre.toFixed(2)}
                </p>
              </div>
              <FaMoneyBill className="text-green-500 text-2xl" />
            </div>
          </div>
          <div className="rounded-lg border border-white/10 bg-e0e3-surface/95 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-e0e3-muted text-sm">Transferencia</p>
                <p className="text-2xl font-bold text-e0e3-foreground">
                  ${((resumen.ingresosPorMedio || {}).transferencia || 0).toFixed(2)}
                </p>
              </div>
              <FaUniversity className="text-blue-500 text-2xl" />
            </div>
          </div>
          <div className="rounded-lg border border-white/10 bg-e0e3-surface/95 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-e0e3-muted text-sm">Tarjeta</p>
                <p className="text-2xl font-bold text-e0e3-foreground">
                  ${((resumen.ingresosPorMedio || {}).tarjeta || 0).toFixed(2)}
                </p>
              </div>
              <FaCreditCard className="text-purple-500 text-2xl" />
            </div>
          </div>
          <div className="rounded-lg border border-white/10 bg-e0e3-surface/95 p-5 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-e0e3-muted text-sm">{BILLETERA_VIRTUAL_LABEL}</p>
                <p className="text-2xl font-bold text-e0e3-foreground">
                  ${((resumen.ingresosPorMedio || {}).mercadopago || 0).toFixed(2)}
                </p>
              </div>
              <FaMobileAlt className="text-indigo-500 text-2xl" />
            </div>
          </div>
        </div>
      )}

      {modoVista === 'diario' && orgId && canVerResumenCaja(hasPermission) && (
        <div className="mb-8">
          <CierreDiarioPanel
            orgId={orgId}
            sucursalId={sucursalId}
            fecha={fecha}
            onFechaChange={setFecha}
            onCierreDataSync={(body) => setEfectivoEsperadoCierre(efectivoEsperadoDesdeCierreDia(body))}
            dark
            canCerrar={canCerrarCaja(hasPermission)}
            canCorregir={canCorregirCierreCaja(hasPermission)}
            showHistorial={canVerHistorialCierres(hasPermission)}
          />
        </div>
      )}

      {/* Formulario de agregar movimiento */}
      <div className="nexo-surface rounded-lg p-6 mb-8">
        <h2 className="text-xl font-semibold text-e0e3-foreground mb-4 flex items-center gap-2">
          <FaPlus className="text-blue-500" />
          Agregar Movimiento
        </h2>

        <form onSubmit={handleAgregar} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-e0e3-muted mb-1">Tipo</label>
            <select
              name="tipo"
              value={form.tipo}
              onChange={handleFormChange}
              className="nexo-field w-full rounded-lg px-3 py-2"
            >
              <option value="ingreso">Ingreso</option>
              <option value="egreso">Egreso</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-e0e3-muted mb-1">Medio</label>
            <select
              name="medio_pago"
              value={form.medio_pago}
              onChange={handleFormChange}
              className="nexo-field w-full rounded-lg px-3 py-2"
            >
              <option value="efectivo">Efectivo</option>
              <option value="tarjeta">Tarjeta</option>
              <option value="transferencia">Transferencia</option>
              <option value="MercadoPago">{BILLETERA_VIRTUAL_LABEL}</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-e0e3-muted mb-1">Monto</label>
            <input
              name="monto"
              type="number"
              step="0.01"
              min="0"
              value={form.monto}
              onChange={handleFormChange}
              placeholder="0.00"
              className="nexo-field w-full rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-e0e3-muted mb-1">Concepto</label>
            <input
              name="concepto"
              value={form.concepto}
              onChange={handleFormChange}
              placeholder="Descripción del movimiento"
              className="nexo-field w-full rounded-lg px-3 py-2"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-e0e3-muted mb-1">Observaciones</label>
            <input
              name="observaciones"
              value={form.observaciones}
              onChange={handleFormChange}
              placeholder="Opcional"
              className="nexo-field w-full rounded-lg px-3 py-2"
            />
          </div>

          <div>
            <Button
              color={form.tipo === 'ingreso' ? 'green' : 'red'}
              type="submit"
              loading={agregando}
              icon={<FaPlus />}
              className="w-full"
            >
              {agregando ? 'Agregando...' : 'Agregar'}
            </Button>
          </div>
        </form>
      </div>

      {/* Tabla de movimientos */}
      <div className="nexo-surface rounded-lg p-6">
        <h2 className="text-xl font-semibold text-e0e3-foreground mb-4">
          {modoVista === 'diario' ? 'Movimientos del día' : 'Movimientos de Caja Chica'}
        </h2>

        {loading ? (
          <div className="flex justify-center py-8">
            <Spinner />
          </div>
        ) : movimientos.length === 0 ? (
          <div className="text-center py-8">
            <FaMoneyBill className="mx-auto text-e0e3-muted/60 text-4xl mb-4" />
            <p className="text-e0e3-muted text-lg">No hay movimientos para esta fecha</p>
            <p className="text-e0e3-muted/80 text-sm">Agrega el primer movimiento usando el formulario de arriba</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-white/[0.04] border-b border-white/10">
                  <th className="p-3 text-left font-medium text-e0e3-muted">Hora</th>
                  <th className="p-3 text-left font-medium text-e0e3-muted">Tipo</th>
                  <th className="p-3 text-right font-medium text-e0e3-muted">Monto</th>
                  <th className="p-3 text-left font-medium text-e0e3-muted">Concepto</th>
                  <th className="p-3 text-left font-medium text-e0e3-muted">Observaciones</th>
                  <th className="p-3 text-center font-medium text-e0e3-muted">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {movimientos.map(mov => (
                  <tr key={mov.id} className={`border-b border-white/10 hover:bg-white/[0.06] transition-colors ${
                    mov.tipo === 'ingreso' ? 'bg-emerald-500/5' : 'bg-red-500/5'
                  }`}>
                    <td className="p-3 text-e0e3-muted">{formatMovimientoHora(mov)}</td>
                    <td className="p-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                        mov.tipo === 'ingreso'
                          ? 'bg-emerald-500/20 text-emerald-300'
                          : 'bg-red-500/20 text-red-300'
                      }`}>
                        {mov.tipo === 'ingreso' ? <FaPlus className="mr-1" /> : <FaMinus className="mr-1" />}
                        {mov.tipo === 'ingreso' ? 'Ingreso' : 'Egreso'}
                      </span>
                    </td>
                    <td className="p-3 text-right font-mono font-semibold">
                      <span className={mov.tipo === 'ingreso' ? 'text-green-600' : 'text-red-600'}>
                        {mov.tipo === 'ingreso' ? '+' : '-'}${parseFloat(mov.monto || 0).toFixed(2)}
                      </span>
                    </td>
                    <td className="p-3 text-e0e3-foreground font-medium">{mov.concepto}</td>
                    <td className="p-3 text-e0e3-muted">{mov.observaciones || '-'}</td>
                    <td className="p-3 text-center">
                      {canEliminarMovimientoCaja(hasPermission) &&
                        String(mov.referencia_tipo || 'manual').toLowerCase() === 'manual' && (
                        <button
                          onClick={() => handleEliminar(mov.id)}
                          disabled={eliminando === mov.id}
                          className="text-red-500 hover:text-red-700 disabled:opacity-50 disabled:cursor-not-allowed p-1 rounded hover:bg-red-500/10 transition-colors"
                          title="Eliminar movimiento manual"
                        >
                          {eliminando === mov.id ? <Spinner size="sm" /> : <FaTrash />}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de verificación de saldo físico */}
      {mostrarVerificacion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="nexo-surface rounded-lg p-6 w-full max-w-md mx-4">
            <h3 className="text-lg font-semibold text-e0e3-foreground mb-4 flex items-center gap-2">
              <FaCalculator className="text-yellow-500" />
              Verificar Saldo Físico
            </h3>

            <p className="text-e0e3-muted mb-4">
              Ingresa el monto que hay físicamente en la caja chica para comparar con el saldo del sistema.
            </p>

            <div className="mb-4">
              <label className="block text-sm font-medium text-e0e3-muted mb-2">Saldo Físico</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={saldoFisico}
                onChange={(e) => setSaldoFisico(e.target.value)}
                placeholder="0.00"
                className="nexo-field w-full rounded-lg px-3 py-2"
              />
            </div>

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setMostrarVerificacion(false);
                  setSaldoFisico('');
                }}
                className="flex-1 px-4 py-2 rounded-lg border border-white/15 text-e0e3-muted hover:bg-white/[0.06] transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={verificarSaldoFisico}
                disabled={verificandoSaldo}
                className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {verificandoSaldo ? <Spinner size="sm" /> : 'Verificar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Caja;
